import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import '../helpdesk/Helpdesk.css';
import './HelpdeskClients.css';

function HelpdeskClients() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [editingClient, setEditingClient] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', company: '' });
    const [saveLoading, setSaveLoading] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });

    useEffect(() => {
        loadClients();
    }, [search, statusFilter, pagination.page]);

    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => setMessage({ text: '', type: '' }), 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const loadClients = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (statusFilter) params.append('status', statusFilter);
            params.append('page', pagination.page);
            params.append('limit', 20);

            const response = await api.get(`/helpdesk-agent/clients?${params.toString()}`);
            if (response.data.success) {
                setClients(response.data.clients);
                setPagination(prev => ({ ...prev, ...response.data.pagination }));
            }
        } catch (error) {
            console.error('Failed to load clients:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (client) => {
        setEditingClient(client.id);
        setEditForm({
            name: client.name || '',
            email: client.email || '',
            phone: client.phone || '',
            company: client.company || ''
        });
    };

    const handleCancelEdit = () => {
        setEditingClient(null);
        setEditForm({ name: '', email: '', phone: '', company: '' });
    };

    const handleSave = async (clientId) => {
        if (!editForm.name.trim() || !editForm.email.trim()) {
            setMessage({ text: 'Name and email are required', type: 'error' });
            return;
        }
        try {
            setSaveLoading(true);
            const response = await api.put(`/helpdesk-agent/clients/${clientId}`, editForm);
            if (response.data.success) {
                setMessage({ text: 'Client updated successfully ✓', type: 'success' });
                setEditingClient(null);
                loadClients();
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'Failed to update client';
            setMessage({ text: msg, type: 'error' });
        } finally {
            setSaveLoading(false);
        }
    };

    const handleToggleStatus = async (client) => {
        const action = client.is_active ? 'deactivate' : 'activate';
        if (!window.confirm(`Are you sure you want to ${action} "${client.name}"?`)) return;

        try {
            setToggleLoading(client.id);
            const response = await api.patch(`/helpdesk-agent/clients/${client.id}/toggle-status`);
            if (response.data.success) {
                setMessage({ 
                    text: `Client ${response.data.is_active ? 'activated' : 'deactivated'} successfully ✓`, 
                    type: 'success' 
                });
                loadClients();
            }
        } catch (error) {
            setMessage({ text: 'Failed to toggle status', type: 'error' });
        } finally {
            setToggleLoading(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    let searchTimeout;
    const handleSearchChange = (e) => {
        const value = e.target.value;
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
            setSearch(value);
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 400);
    };

    return (
        <Layout>
        <div className="hd-clients-page">
            {/* Toast Message */}
            {message.text && (
                <div className={`hd-save-message ${message.type}`}>{message.text}</div>
            )}

            {/* Header */}
            <div className="hd-clients-header">
                <div>
                    <h1>👥 Clients Management</h1>
                    <p className="hd-clients-subtitle">{pagination.total} total clients</p>
                </div>
            </div>

            {/* Filters */}
            <div className="hd-clients-filters">
                <div className="hd-search-box">
                    <span className="hd-search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Search by name, email, phone, or company..."
                        onChange={handleSearchChange}
                        className="hd-search-input"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="hd-filter-select"
                >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>

            {/* Table */}
            {loading ? (
                <div className="hd-loading">Loading...</div>
            ) : clients.length === 0 ? (
                <div className="hd-empty">No clients found</div>
            ) : (
                <div className="hd-clients-table-wrap">
                    <table className="hd-clients-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Company</th>
                                <th>Tickets</th>
                                <th>Joined</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map(client => (
                                <React.Fragment key={client.id}>
                                    <tr className={`hd-client-row ${!client.is_active ? 'inactive' : ''} ${editingClient === client.id ? 'editing' : ''}`}>
                                        <td className="hd-client-name">
                                            {client.name}
                                        </td>
                                        <td>{client.email}</td>
                                        <td>{client.phone || <span className="hd-muted">-</span>}</td>
                                        <td>{client.all_companies ? client.all_companies.split('||').map((comp, i) => (
                                                <span key={i} className="hd-company-tag">{comp.trim()}</span>
                                            )) : (client.company ? <span className="hd-company-tag">{client.company}</span> : <span className="hd-muted">-</span>)}</td>
                                        <td>
                                            <span className="hd-ticket-count">{client.ticket_count || 0}</span>
                                        </td>
                                        <td className="hd-date">{formatDate(client.created_at)}</td>
                                        <td>
                                            <span className={`hd-status-badge ${client.is_active ? 'active' : 'inactive'}`}>
                                                {client.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="hd-actions">
                                            <button
                                                className="hd-btn-edit"
                                                onClick={() => editingClient === client.id ? handleCancelEdit() : handleEdit(client)}
                                                title={editingClient === client.id ? 'Cancel' : 'Edit'}
                                            >
                                                {editingClient === client.id ? '✕' : '✏️'}
                                            </button>
                                            <button
                                                className={`hd-btn-toggle ${client.is_active ? 'deactivate' : 'activate'}`}
                                                onClick={() => handleToggleStatus(client)}
                                                disabled={toggleLoading === client.id}
                                                title={client.is_active ? 'Deactivate' : 'Activate'}
                                            >
                                                {toggleLoading === client.id ? '...' : (client.is_active ? '🔒' : '🔓')}
                                            </button>
                                        </td>
                                    </tr>

                                    {/* Inline Edit Row */}
                                    {editingClient === client.id && (
                                        <tr className="hd-edit-row">
                                            <td colSpan="8">
                                                <div className="hd-edit-form">
                                                    <div className="hd-edit-fields">
                                                        <div className="hd-field">
                                                            <label>Name *</label>
                                                            <input
                                                                type="text"
                                                                value={editForm.name}
                                                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                                                className="hd-input"
                                                            />
                                                        </div>
                                                        <div className="hd-field">
                                                            <label>Email *</label>
                                                            <input
                                                                type="email"
                                                                value={editForm.email}
                                                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                                                className="hd-input"
                                                            />
                                                        </div>
                                                        <div className="hd-field">
                                                            <label>Phone</label>
                                                            <input
                                                                type="text"
                                                                value={editForm.phone}
                                                                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                                                className="hd-input"
                                                            />
                                                        </div>
                                                        <div className="hd-field">
                                                            <label>Company</label>
                                                            <input
                                                                type="text"
                                                                value={editForm.company}
                                                                onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                                                                className="hd-input"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="hd-edit-actions">
                                                        <button
                                                            className="hd-btn hd-btn-save"
                                                            onClick={() => handleSave(client.id)}
                                                            disabled={saveLoading}
                                                        >
                                                            {saveLoading ? 'Saving...' : '💾 Save'}
                                                        </button>
                                                        <button className="hd-btn hd-btn-cancel" onClick={handleCancelEdit}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="hd-pagination">
                    <button
                        disabled={pagination.page <= 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        className="hd-btn hd-btn-page"
                    >
                        ← Previous
                    </button>
                    <span className="hd-page-info">
                        Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        className="hd-btn hd-btn-page"
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
        </Layout>
    );
}

export default HelpdeskClients;
